// hooks/domain/useChapterViewerRole.ts
import { useEffect, useState } from 'react';
import { apiFetch } from '@/api/core/apiFetch';
import type { ChapterRole as ChapterRoleValue } from '@/types/chapter.types';

/** Ruolo del viewer: null finché la risposta non è arrivata. */
type ChapterRole = ChapterRoleValue | null;

/** Stato derivato dello hook — usato da App.tsx e ChapterDashboard.tsx */
export type ViewerRoleStatus = 'loading' | 'ok' | 'not_member' | 'error';

export interface UseChapterViewerRoleResult {
  /** Stato derivato — preferire questo ai flag booleani nei nuovi consumer */
  status: ViewerRoleStatus;
  role: ChapterRole;
  membershipRole: string | null;
  isOwner: boolean;
  isOfficer: boolean;
  /** @deprecated usare status === 'loading' */
  loading: boolean;
  /** @deprecated usare status === 'error' */
  error: string | null;
}

export function useChapterViewerRole(
  chapterId: string | undefined
): UseChapterViewerRoleResult {
  const [role, setRole] = useState<ChapterRole>(null);
  const [membershipRole, setMembershipRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chapterId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // apiFetch does not accept type arguments — cast the result instead
        const data = (await apiFetch(
          `/api/chapters/${chapterId}/my-role`
        )) as { role: ChapterRole; membershipRole: string | null };
        if (!cancelled) {
          setRole(data.role);
          setMembershipRole(data.membershipRole);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Errore sconosciuto');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chapterId]);

  // ── status derivato ──────────────────────────────────────────────────────
  const status: ViewerRoleStatus = loading
    ? 'loading'
    : error
    ? 'error'
    : role
    ? 'ok'
    : 'not_member';

  return {
    status,
    role,
    membershipRole,
    isOwner: role === 'chapter_owner',
    isOfficer:
      role === 'primary_officer' ||
      role === 'activity_officer' ||
      role === 'chapter_owner',
    loading,
    error,
  };
}
